// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/admin/settings/release-notes/:id\",\"regex\":\"^/admin/settings/release-notes/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/users/:id\",\"regex\":\"^/admin/users/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/users/:id/subscription\",\"regex\":\"^/admin/users/([^/]+)/subscription$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/waivers/:id\",\"regex\":\"^/admin/waivers/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/dashboard/waivers/:id\",\"regex\":\"^/dashboard/waivers/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/sign-in/:sign-in*?\",\"regex\":\"^/sign-in(?:/(.*))?$\",\"paramNames\":[\"sign-in\"],\"hasOptionalPrefix\":false},{\"path\":\"/sign-up/:sign-up*?\",\"regex\":\"^/sign-up(?:/(.*))?$\",\"paramNames\":[\"sign-up\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/admin\"},{\"path\":\"/admin/activity-logs\"},{\"path\":\"/admin/analytics/capacity\"},{\"path\":\"/admin/announcements\"},{\"path\":\"/admin/birthdays\"},{\"path\":\"/admin/calendar\"},{\"path\":\"/admin/cart\"},{\"path\":\"/admin/checkin\"},{\"path\":\"/admin/checkin/stats\"},{\"path\":\"/admin/class-programs\"},{\"path\":\"/admin/classes\"},{\"path\":\"/admin/dashboard\"},{\"path\":\"/admin/email-events\"},{\"path\":\"/admin/emails\"},{\"path\":\"/admin/freeze-requests\"},{\"path\":\"/admin/instructors\"},{\"path\":\"/admin/inventory\"},{\"path\":\"/admin/posthog\"},{\"path\":\"/admin/products\"},{\"path\":\"/admin/rate-limit-monitor\"},{\"path\":\"/admin/reports\"},{\"path\":\"/admin/revenue\"},{\"path\":\"/admin/route-permissions\"},{\"path\":\"/admin/settings\"},{\"path\":\"/admin/settings/release-notes\"},{\"path\":\"/admin/settings/release-notes/manage\"},{\"path\":\"/admin/subscriptions\"},{\"path\":\"/admin/surveys\"},{\"path\":\"/admin/templates\"},{\"path\":\"/admin/terminal\"},{\"path\":\"/admin/users\"},{\"path\":\"/admin/waivers\"},{\"path\":\"/admin/waivers/blank\"},{\"path\":\"/admin/whats-new\"},{\"path\":\"/auth/redirect\"},{\"path\":\"/cookie-policy\"},{\"path\":\"/dashboard\"},{\"path\":\"/dashboard/bookings\"},{\"path\":\"/dashboard/household\"},{\"path\":\"/dashboard/qr-code\"},{\"path\":\"/dashboard/settings\"},{\"path\":\"/dashboard/stats\"},{\"path\":\"/dashboard/subscriptions\"},{\"path\":\"/dashboard/waivers\"},{\"path\":\"/onboarding\"},{\"path\":\"/privacy-policy\"},{\"path\":\"/refund-policy\"},{\"path\":\"/sentry-example-page\"},{\"path\":\"/survey/respond\"},{\"path\":\"/terms\"},{\"path\":\"/waiver\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.0.10";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

const COOKIE_CONSENT_KEY = "cookie-consent";

// Helper function to get cookie preferences
function getCookiePreferences() {
  if (typeof window === "undefined") return null;
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!consent) return null;
  try {
    return JSON.parse(consent);
  } catch {
    return null;
  }
}

// Initialize Sentry only if error tracking is consented and in production
function initializeSentry() {
  // Don't initialize Sentry in development
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  const preferences = getCookiePreferences();
  if (preferences?.errorTracking !== false) {
    Sentry.init({
      dsn: "https://3f3c7e9a1cb619c8a64ad5f280367b7b@o4510678215950336.ingest.us.sentry.io/4510678217785344",
      integrations: [Sentry.replayIntegration()],
      tracesSampleRate: 1,
      enableLogs: true,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: true,
    });
  }
}

// Initialize PostHog only if analytics is consented and in production
function initializePostHog() {
  // Don't initialize PostHog in development
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  const preferences = getCookiePreferences();
  if (preferences?.analytics !== false && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: "/ingest",
      ui_host: "https://us.posthog.com",
      defaults: '2025-05-24',
      capture_exceptions: true,
      debug: false, // No debug in production
    });
  }
}

// Track initialization state
let sentryInitialized = false;
let posthogInitialized = false;

// Initialize on load if consent already given
if (typeof window !== "undefined") {
  const preferences = getCookiePreferences();
  
  // Only initialize if consent was already given
  if (preferences) {
    // Only initialize Sentry in production
    if (preferences.errorTracking !== false && process.env.NODE_ENV === 'production') {
      initializeSentry();
      sentryInitialized = true;
    }
    // Only initialize PostHog in production
    if (preferences.analytics !== false && process.env.NODE_ENV === 'production') {
      initializePostHog();
      posthogInitialized = true;
    }
  }

  // Listen for consent changes
  window.addEventListener("cookieConsentChanged", ((event: CustomEvent) => {
    const preferences = event.detail;
    
    // Handle Sentry (only in production)
    if (preferences.errorTracking && !sentryInitialized && process.env.NODE_ENV === 'production') {
      initializeSentry();
      sentryInitialized = true;
    } else if (!preferences.errorTracking && sentryInitialized) {
      // Note: Sentry doesn't have a simple way to disable it once initialized
      // In practice, you'd need to reload the page or use Sentry's client.close()
      console.log("Error tracking consent withdrawn. Please reload the page for changes to take effect.");
    }

    // Handle PostHog (only in production)
    if (preferences.analytics && !posthogInitialized && process.env.NEXT_PUBLIC_POSTHOG_KEY && process.env.NODE_ENV === 'production') {
      initializePostHog();
      posthogInitialized = true;
    } else if (!preferences.analytics && posthogInitialized) {
      posthog.reset();
      posthogInitialized = false;
    }
  }) as EventListener);
}

// Only export Sentry router transition tracking in production
export const onRouterTransitionStart = process.env.NODE_ENV === 'production' 
  ? Sentry.captureRouterTransitionStart 
  : undefined;
